import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flow } from '@thive/fddm-types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';

import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { UsageProfileOptions } from '@/modules/flowsV2/types';

import { useFlowUpdaters } from '../hooks/useFlowUpdaters';
import { LoadPerHourInput } from '.';
import UsageProfileComponent from './UsageProfileComponent';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';

interface Props {
  id: string;
  disabled: boolean;
  flow: Flow;
}

function GoodsToPersonsPropertiesComponent({ id, disabled, flow }: Props) {
  const { t } = useTranslation();
  const {
    onConcurrentAisleCountChange,
    onSequencingHorizonWindowChange,
    onHandoverCooldownChange,
    onProcessDurationChange,
  } = useFlowUpdaters(id);

  const { lassies } = useFloorplanDesignStore((state) => state.design.templates);

  const lassieTemplateGroupNumAisles = useMemo(
    () => (lassies.length > 0 ? lassies[0].params.numAisles : undefined),
    [lassies],
  );

  const lassieMiniTemplateGroupNumAisles = useMemo(
    () => (lassies.length > 0 ? lassies[0].params.numAisles : undefined),
    [lassies],
  );

  const numberOfAisles = useMemo(() => {
    if (lassieTemplateGroupNumAisles) {
      return lassieTemplateGroupNumAisles;
    }
    if (lassieMiniTemplateGroupNumAisles) {
      return lassieMiniTemplateGroupNumAisles;
    }
    return 0;
  }, [lassieTemplateGroupNumAisles, lassieMiniTemplateGroupNumAisles]);

  const handleSequencingHorizonWindowChange = useCallback(
    (value: number) => onSequencingHorizonWindowChange(value),
    [onSequencingHorizonWindowChange],
  );

  const handleHandoverCooldownChange = useCallback(
    (value: number) => onHandoverCooldownChange(value),
    [onHandoverCooldownChange],
  );

  const handleProcessDurationChange = useCallback(
    (value: number) => onProcessDurationChange(value),
    [onProcessDurationChange],
  );

  const handleConcurrentAisleCountChange = useCallback(
    (value: number) => {
      onConcurrentAisleCountChange(value);
    },
    [onConcurrentAisleCountChange],
  );

  const [expanded, setExpanded] = useState<UsageProfileOptions | false>(false);
  const handleAccordionChange =
    (option: UsageProfileOptions) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? option : false);
    };

  const { goodsToPersonsExtension } = flow;
  const loadsPerHour = useMemo(() => {
    if (flow.hourlyOrderCounts && flow.hourlyOrderCounts.length > 0) {
      return flow.hourlyOrderCounts[0];
    }
    return 0;
  }, [flow.hourlyOrderCounts]);

  if (!goodsToPersonsExtension) return null;

  const { aisleProfile, shelfProfile, depthProfile } = goodsToPersonsExtension;
  if (aisleProfile === undefined || shelfProfile === undefined || depthProfile === undefined)
    return null;
  return (
    <>
      <PropertiesLabel
        labelRatio={70}
        i18nkey='interface:properties.flows.flow_info.total_loads.label'
      >
        <LoadPerHourInput id={id} totalNumLoads={loadsPerHour} disabled={disabled} />
      </PropertiesLabel>

      <PropertiesLabel
        labelRatio={70}
        i18nkey='interface:properties.flows.goods_to_persons.fields.sequencing_horizon_window.label'
      >
        <NumberInput
          value={goodsToPersonsExtension.sequencingHorizonWindow}
          minValue={0}
          minValueErrorMsg={t('common:minimum_value', { minValue: 0 })}
          unit={t('interface:settings.units.seconds')}
          onChange={handleSequencingHorizonWindowChange}
        />
      </PropertiesLabel>
      <PropertiesLabel
        labelRatio={70}
        i18nkey='interface:properties.flows.goods_to_persons.fields.handover_cooldown.label'
      >
        <NumberInput
          value={goodsToPersonsExtension.handoverCooldown}
          minValue={0}
          minValueErrorMsg={t('common:minimum_value', { minValue: 0 })}
          unit={t('interface:settings.units.seconds')}
          onChange={handleHandoverCooldownChange}
        />
      </PropertiesLabel>
      <PropertiesLabel
        labelRatio={70}
        i18nkey='interface:properties.flows.goods_to_persons.fields.process_duration.label'
      >
        <NumberInput
          value={goodsToPersonsExtension.processDuration}
          minValue={0}
          minValueErrorMsg={t('common:minimum_value', { minValue: 0 })}
          unit={t('interface:settings.units.seconds')}
          onChange={handleProcessDurationChange}
        />
      </PropertiesLabel>
      <PropertiesLabel
        labelRatio={70}
        skipFocus
        i18nkey='interface:properties.flows.goods_to_persons.fields.num_vehicles.label'
      >
        <NumberInput
          value={goodsToPersonsExtension.concurrentAisleCount}
          onChange={handleConcurrentAisleCountChange}
          minValue={1}
          maxValue={numberOfAisles}
          minValueErrorMsg={t('common:minimum_value', { minValue: 1 })}
          maxValueErrorMsg={t('common:maximum_value', { maxValue: numberOfAisles })}
        />
      </PropertiesLabel>
      <Accordion
        expanded={expanded === 'aisleProfile'}
        onChange={handleAccordionChange('aisleProfile')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            {t<string>('interface:properties.flows.goods_to_persons.usage_profiles.aisleProfile')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <UsageProfileComponent
            flowId={flow.id}
            usageProfile={aisleProfile}
            option='aisleProfile'
          />
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded === 'shelfProfile'}
        onChange={handleAccordionChange('shelfProfile')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            {t<string>('interface:properties.flows.goods_to_persons.usage_profiles.shelfProfile')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <UsageProfileComponent
            flowId={flow.id}
            usageProfile={shelfProfile}
            option='shelfProfile'
          />
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded === 'depthProfile'}
        onChange={handleAccordionChange('depthProfile')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            {t<string>('interface:properties.flows.goods_to_persons.usage_profiles.depthProfile')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <UsageProfileComponent
            flowId={flow.id}
            usageProfile={depthProfile}
            option='depthProfile'
          />
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded === 'processAreaUsage'}
        onChange={handleAccordionChange('processAreaUsage')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            {t<string>(
              'interface:properties.flows.goods_to_persons.usage_profiles.processAreaUsage',
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <UsageProfileComponent
            flowId={flow.id}
            usageProfile={goodsToPersonsExtension.processAreaUsage}
            option='processAreaUsage'
          />
        </AccordionDetails>
      </Accordion>
    </>
  );
}

export const GoodsToPersonsProperties = memo(GoodsToPersonsPropertiesComponent);
